import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { updatePreferences } from '@/lib/meApi';

/**
 * Whether the sidebar is collapsed.
 *
 * This belongs to the PERSON, not the browser. It was persisted to localStorage alone, so a
 * barrister who collapsed it in chambers found it expanded again at home, and
 * `UserPreference.sidebar_collapsed` was written by Settings and read by nothing.
 *
 * localStorage stays as an IMMEDIATE cache — the shell must lay out in the first frame rather
 * than wait for a request — and the account is the source of truth, adopted once per session by
 * `PreferenceEffects` and written back on every toggle.
 */
interface SidebarState {
  collapsed: boolean;
  /** True once this session has taken the account's value, so it is not re-applied over a toggle. */
  adoptedFromAccount: boolean;
  toggle: () => void;
  setCollapsed: (v: boolean, options?: { persist?: boolean }) => void;
  markAdopted: () => void;
}

/** Save without ever letting a preference write interrupt what the person is doing. */
function persistToAccount(collapsed: boolean): void {
  void updatePreferences({ sidebarCollapsed: collapsed }).catch(() => {
    // The choice still applies here; the next successful save or session reconciles it.
  });
}

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set, get) => ({
      collapsed: false,
      adoptedFromAccount: false,

      toggle: () => {
        const next = !get().collapsed;
        set({ collapsed: next });
        persistToAccount(next);
      },

      setCollapsed: (v, options) => {
        if (get().collapsed === v) return;
        set({ collapsed: v });
        if (options?.persist !== false) persistToAccount(v);
      },

      markAdopted: () => set({ adoptedFromAccount: true }),
    }),
    {
      name: 'jurivoxa-sidebar',
      // `adoptedFromAccount` is per-session: a new tab must read the account again.
      partialize: (state) => ({ collapsed: state.collapsed }),
    },
  ),
);
